import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

interface LogItem {
  id: string;
  paper_id: string;
  paper_url: string;
  status: string;
  google_status: string | null;
  google_error: string | null;
  bing_status: string | null;
  bing_error: string | null;
  attempt_number: number;
  processing_time_ms: number | null;
  created_at: string;
  papers: { id: string; title: string } | null;
}

interface IndexingLogProps {
  logs: LogItem[] | undefined;
}

export const IndexingLog = ({ logs }: IndexingLogProps) => {
  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'success': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'skipped': return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default: return <span className="text-gray-400">-</span>;
    }
  };

  const getOverallStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!logs || logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No logs available
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Paper</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Google</TableHead>
            <TableHead>Bing</TableHead>
            <TableHead>Attempt</TableHead>
            <TableHead>Time (ms)</TableHead>
            <TableHead>When</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => (
            <TableRow key={log.id}>
              <TableCell className="max-w-xs truncate">
                {log.papers?.title || 'Unknown Paper'}
              </TableCell>
              <TableCell>
                <Badge className={getOverallStatusColor(log.status)}>
                  {log.status}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getStatusIcon(log.google_status)}
                  {log.google_error && (
                    <span className="text-xs text-muted-foreground truncate max-w-[150px]" title={log.google_error}>
                      {log.google_error}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getStatusIcon(log.bing_status)}
                  {log.bing_error && (
                    <span className="text-xs text-muted-foreground truncate max-w-[150px]" title={log.bing_error}>
                      {log.bing_error}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell>{log.attempt_number}</TableCell>
              <TableCell>{log.processing_time_ms || '-'}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
